import { useEffect, useRef } from "react";

// 0074, 0051 S1c: the one settle time of every one-tap founder act in Review.
// An act is never taken from a tap or a key the founder could not have aimed
// at it (a quick second tap on the act he just tapped lands on whatever the
// move put in the same place): for this long after the act appears, or after a
// move an act caused shows it, it ignores every activation, a pointer's or a
// key's (a key presses a button through its click), and it never takes the
// click that ends a press it did not see begin. Review's keyboard guard holds a
// card's key acts for the same time after an act's move. Silent: nothing on
// screen changes.
export const ACT_SETTLE_MS = 500;

// ---- The act-move hold (0051 S1c, the keyboard guard) ------------------------

// The card the last forward move AN ACT caused showed, and when it became the
// current one (`pending` until it renders). Review writes it (views/Review.tsx:
// an act's forward move opens it, the founder's own move ends it, and the card
// rendering marks it shown); Review's capture-phase key guard, DecisionCard's
// Confirm and the covered-by row's Yes read it. Confirm and the covered-by Yes
// take no click while the hold is open (the founder's ruling of 2026-10-19,
// reversing 2026-10-07's "Clicks are unaffected" for these acts). A card the
// founder reached by their own navigation is not held.
type ActMove = { id: string; at: number; pending: boolean };
let actMove: ActMove | null = null;

// The record as it stands (Review's card effect reads it).
export function actMoveRecord(): Readonly<ActMove> | null {
  return actMove;
}

// An act's forward move to the card `id`: the hold opens, pending until that
// card renders.
export function openActMoveHold(id: string): void {
  actMove = { id, at: Date.now(), pending: true };
}

// The founder's own move, the founder on another card, or Review leaving: no
// hold.
export function endActMoveHold(): void {
  actMove = null;
}

// The card an act's move showed is now the current one: its clock starts.
export function markActMoveShown(id: string): void {
  if (actMove !== null && actMove.id === id && actMove.pending) actMove = { id, at: Date.now(), pending: false };
}

// The hold is open: an act's move is on its way to a card (pending), or showed
// `cardId` (any card when no id is given) less than ACT_SETTLE_MS ago.
export function actMoveHoldOpen(cardId?: string): boolean {
  const rec = actMove;
  return (
    rec !== null && (rec.pending || cardId === undefined || rec.id === cardId) && Date.now() - rec.at < ACT_SETTLE_MS
  );
}

// ---- The settle guard --------------------------------------------------------

// useSettleGuard is the settle rule for one act's button. Its clock starts in
// one of two ways: by default when the button mounts, and again whenever
// `resetKey` changes (an act that moved, or whose place changed, is a new
// target; with no key it starts at mount only) - the confirm option's Yes and
// every act of the shared confirm card; with `afterActMove`, only when a move
// an act caused shows the card (the act-move hold above), never after the
// founder's own navigation - DecisionCard's Confirm and the covered-by row's
// Yes. Either way the click that ends a press the button did not see begin is
// never taken, and a held key acts once. It hands back the settled test, the
// click wrapper, the pointer handlers and the key handler the button wires.
export function useSettleGuard(opts: { resetKey?: string; afterActMove?: boolean } = {}): {
  settled: () => boolean;
  onClick: (act: () => void) => void;
  onPointerDown: () => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onKeyDown: (e: { key: string; repeat: boolean; preventDefault: () => void }) => void;
} {
  const { resetKey, afterActMove = false } = opts;
  // When the act was shown (or last moved), whether the act saw the pointer
  // press now down on it begin, and when it last saw a release whose press it
  // did not see begin (the click that release ends is stray).
  const shownAt = useRef<number | null>(null);
  useEffect(() => {
    shownAt.current = Date.now();
  }, [resetKey]);
  const settled = () =>
    afterActMove
      ? !actMoveHoldOpen()
      : shownAt.current !== null && Date.now() - shownAt.current >= ACT_SETTLE_MS;
  const pressSeen = useRef(false);
  const strayReleaseAt = useRef<number | null>(null);

  // The act's click - a tap's, or a key's on the focused button - is taken
  // only once it has settled, and never when it ends a press the button did
  // not see begin. A stray release marks only a click that follows it within
  // the settle time, so a later key or tap is never refused for it.
  const onClick = (act: () => void) => {
    const stray = strayReleaseAt.current !== null && Date.now() - strayReleaseAt.current < ACT_SETTLE_MS;
    strayReleaseAt.current = null;
    if (!settled() || stray) return;
    act();
  };

  return {
    settled,
    onClick,
    // A press the button saw begin is aimed at it; a release it did not see
    // begin (the press started on what was there before) ends a stray click.
    onPointerDown: () => {
      pressSeen.current = true;
      strayReleaseAt.current = null;
    },
    onPointerUp: () => {
      if (!pressSeen.current) strayReleaseAt.current = Date.now();
      pressSeen.current = false;
    },
    onPointerCancel: () => {
      pressSeen.current = false;
    },
    // A held key activates the act once: its auto-repeats are not presses.
    // Before the act settles no key press activates it.
    onKeyDown: (e) => {
      if ((e.key === "Enter" || e.key === " ") && (e.repeat || !settled())) e.preventDefault();
    },
  };
}
